import { analytics } from '@/modules/common/lib/segment';

import isCrawlerUserAgent from '@/utils/isCrawlerUserAgent';

import { shouldBlockAnalytics } from './analyticsGuard';
import { initDataLayer } from './dataLayer';

const GTM_LOAD_TIMEOUT_MS = 10_000;

type GtmState = 'waiting' | 'loaded' | 'timed_out';
type DataLayerEvent = Record<string, unknown>;

let gtmState: GtmState = 'waiting';
let pendingEvents: DataLayerEvent[] = [];
let gateInitialized = false;

function isGtmLoadEvent(item: unknown): boolean {
  return !!item && typeof item === 'object' && 'event' in item && item.event === 'gtm.load';
}

function markLoaded(cleanup: () => void) {
  gtmState = 'loaded';
  cleanup();
  flushPendingEvents();
}

function initGtmGate() {
  if (gateInitialized || typeof window === 'undefined') {
    return;
  }
  gateInitialized = true;

  initDataLayer();

  if (window.dataLayer?.some(isGtmLoadEvent)) {
    gtmState = 'loaded';
    return;
  }

  let pollId: ReturnType<typeof setInterval>;
  let timeoutId: ReturnType<typeof setTimeout>;

  const cleanup = () => {
    clearInterval(pollId);
    clearTimeout(timeoutId);
    window.removeEventListener('datalayerpush', handlePush);
  };

  // Primary: CustomEvent from our wrapped push (fires if GTM hasn't overwritten push yet)
  const handlePush = (event: Event) => {
    if ((event as CustomEvent<DataLayerEvent>).detail?.event === 'gtm.load') {
      markLoaded(cleanup);
    }
  };
  window.addEventListener('datalayerpush', handlePush);

  // Fallback: GTM overwrites .push(), so our CustomEvent may never fire.
  // Poll the array directly to catch gtm.load regardless.
  pollId = setInterval(() => {
    if (window.dataLayer?.some(isGtmLoadEvent)) {
      markLoaded(cleanup);
    }
  }, 200);

  timeoutId = setTimeout(() => {
    if (gtmState === 'waiting') {
      gtmState = 'timed_out';
      cleanup();
      flushPendingEvents();
    }
  }, GTM_LOAD_TIMEOUT_MS);
}

function flushPendingEvents() {
  const events = [...pendingEvents];
  pendingEvents = [];
  events.forEach(dispatchEvent);
}

function dispatchEvent(data: DataLayerEvent) {
  if (gtmState === 'loaded' && window.dataLayer?.push) {
    window.dataLayer.push(data);
  } else {
    const eventName = typeof data.event === 'string' ? data.event : 'gtm_event';
    analytics.track(eventName, data);
  }
}

export function sendGTMEvent(data: DataLayerEvent) {
  if (typeof window === 'undefined' || isCrawlerUserAgent() || shouldBlockAnalytics()) {
    return;
  }

  initGtmGate();

  if (gtmState === 'waiting') {
    pendingEvents.push(data);
  } else {
    dispatchEvent(data);
  }
}
